import { isLeft } from 'fp-ts/lib/Either';
import { chainW } from 'fp-ts/lib/TaskEither';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { usePageTitle } from '../../hooks/usePageTitle';
import { useTRPC } from '../../hooks/useTRPC';
import * as Form from '../Form';
import { TwoPanelsWithSeekerHero } from '../TwoPanelsWithSeekerHero';
import { VerificationCodeInput } from '../VerificationCodeInput';

interface Props {
  className?: string;
}

export function ForgotPasswordPage(props: Props) {
  const navigate = useNavigate();
  const trpc = useTRPC();
  const [isVerificationStep, setIsVerificationStep] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  usePageTitle('Forgot Password | Publishing Portal');

  const handleRequestReset = chainW((data: { email: string }) => async () => {
    const result = await trpc.requestPasswordReset.mutate({
      email: data.email,
    });

    if (isLeft(result)) {
      const error = result.left;
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error && 'message' in error
            ? String(error.message)
            : 'Failed to send reset code';
      throw new Error(message);
    }

    setEmail(data.email);
    setIsVerificationStep(true);
    return result;
  });

  const handleVerifyAndReset = chainW(
    (data: { newPassword: string; confirmPassword: string }) => async () => {
      const result = await trpc.resetPasswordWithCode.mutate({
        email,
        code: verificationCode,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      });

      if (isLeft(result)) {
        const error = result.left;
        const message =
          error instanceof Error
            ? error.message
            : typeof error === 'object' && error && 'message' in error
              ? String(error.message)
              : 'Failed to reset password';
        throw new Error(message);
      }

      toast.success(
        'Password successfully changed! Please log in with your new password.',
      );
      navigate('/login');
      return result;
    },
  );

  if (isVerificationStep) {
    return (
      <TwoPanelsWithSeekerHero className={props.className}>
        <div className="grid place-items-center h-full">
          <Form.Root
            key="verification-form"
            autoComplete="off"
            className="flex flex-col items-center w-[70%]"
            formDataShape={z.object({
              newPassword: z
                .string()
                .min(6, 'Password must be at least 6 characters'),
              confirmPassword: z
                .string()
                .min(1, 'Please confirm your password'),
            })}
            validityKey="verify"
            onComplete={() => navigate('/dashboard')}
            onSubmitData={(data) => {
              if (verificationCode.length !== 6) {
                throw new Error('Please enter a 6-digit verification code');
              }
              return handleVerifyAndReset(data);
            }}
          >
            <div className="text-4xl font-medium text-white max-w-[400px] text-center">
              Reset Your Password
            </div>
            <p className="text-sm text-neutral-400 mt-4 text-center max-w-[350px]">
              Enter the 6-digit code sent to {email} and your new password.
            </p>
            <div className="w-full mt-8">
              <label className="block">
                <div className="font-bold text-white text-sm mb-1">
                  Verification Code
                </div>
                <VerificationCodeInput
                  value={verificationCode}
                  onChange={setVerificationCode}
                  autoFocus
                  className="w-full"
                />
              </label>
            </div>
            <Form.Input
              required
              className="w-full mt-6"
              label="New Password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
            />
            <Form.Input
              required
              className="w-full mt-6"
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
            />
            <div className="mt-12 w-full relative">
              <Form.Submit className="w-full" type="submit">
                Reset Password
              </Form.Submit>
              <Form.SubmissionError />
            </div>
            <div className="mt-6 text-white text-xs">
              <button
                type="button"
                onClick={() => {
                  setIsVerificationStep(false);
                  setVerificationCode('');
                }}
                className="underline hover:no-underline"
              >
                Back to email
              </button>
            </div>
          </Form.Root>
        </div>
      </TwoPanelsWithSeekerHero>
    );
  }

  return (
    <TwoPanelsWithSeekerHero className={props.className}>
      <div className="grid place-items-center h-full">
        <Form.Root
          key="email-form"
          autoComplete="off"
          className="flex flex-col items-center w-[70%]"
          formDataShape={z.object({
            email: z.string().email('Please enter a valid email'),
          })}
          onComplete={() => {}}
          onSubmitData={handleRequestReset}
        >
          <div className="text-4xl font-medium text-white max-w-[400px] text-center">
            Forgot Password?
          </div>
          <p className="text-sm text-neutral-400 mt-4 text-center max-w-[350px]">
            Enter your email address and we'll send you a verification code to
            reset your password.
          </p>
          <Form.Input
            required
            className="w-full mt-12"
            label="Email Address"
            name="email"
            type="email"
            autoComplete="email"
          />
          <div className="mt-12 w-full relative">
            <Form.Submit className="w-full" type="submit">
              Send Reset Code
            </Form.Submit>
            <Form.SubmissionError />
          </div>
          <div className="mt-6 text-white text-xs">
            Remember your password?{' '}
            <a href="/login" className="underline hover:no-underline">
              Log in
            </a>
          </div>
        </Form.Root>
      </div>
    </TwoPanelsWithSeekerHero>
  );
}
